if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantitySelect = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input[name="quantity"]');
        this.quantitySelect = this.querySelector('select[name="quantity"]');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.quantitySelect = this.quantityForm.querySelector('select[name="quantity"]');

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        
        // FIX: Clear the wrap-around scanner flag every time a new option is clicked
        this.hasWrappedAround = false; 

        let productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.onCard === 'true' ? false : this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl,target),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // FIX #1: Re-implemented the Dropdown Selector Null Guard
            if (targetId && targetId.trim() !== '') {
              if (document.querySelector(`#${targetId}`)) {
                document.querySelector(`#${targetId}`)?.focus();
              }
            }
          })
          .catch((error) => {
            // FIX #2: Managed stacked browser abort fetch errors
            if (error.name === 'AbortError' || error.message?.includes('abort')) {
              console.log('Fetch safely neutralized by variant auto-switch.');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (this.dataset.quickAdd === 'true') {
          params.push(`view=quick-add`)
        }

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, target) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (this.dataset.onCard != 'true' && variant.price) {
            updateSavingsBarsPrice(variant.price);
          }

          this.pickupAvailability?.update(variant);
          
          if (this.dataset.onCard != 'true') {
            this.updateOptionValues(html);

            if (window.matchMedia('(max-width: 749px)').matches) {
              const saved = this.getAttribute('data-color-scroll');
              if (saved != null) {
                requestAnimationFrame(() => requestAnimationFrame(() => {
                  const slider = this.querySelector('variant-selects .color-swatch-slider .slider');
                  if (slider) {
                    if(target.nodeName == 'OPTION') {
                      const selectedSwatch = slider.querySelector('fieldset input[type="radio"]:checked').nextElementSibling;
                      const leftPosition = selectedSwatch.offsetLeft;
                      slider.scrollLeft = leftPosition - 16;
                    } else {
                      const prevSnap = slider.style.scrollSnapType;
                      const prevBehavior = slider.style.scrollBehavior;
                      slider.style.scrollSnapType = 'none';
                      slider.style.scrollBehavior = 'auto';

                      const maxLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
                      slider.scrollLeft = Math.min(parseInt(saved, 10) || 0, maxLeft);

                      slider.style.scrollBehavior = prevBehavior || '';
                      slider.style.scrollSnapType = prevSnap || '';
                      this.removeAttribute('data-color-scroll');
                    }
                  }
                }));
              }
            }
            this.updateURL(productUrl, variant?.id);
          } else if (this.dataset.showVariantPrice == 'true') {
            this.updatePricePerVariant(productUrl, variant);
          } else {
            this.updateCardPrice(productUrl);
          }

          this.updateVariantInputs(variant?.id);

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('temp-hidden', shouldHide(source));
            }
          };

          // FIX #3: Split Out of Stock logic completely from Non-existent variants
          if (!variant.available) {
            this.productForm?.toggleSubmitButton(
              true,
              window.variantStrings?.soldOut || 'Sold Out'
            );

            updateSourceFromDestination('price');
            updateSourceFromDestination('price1');
            updateSourceFromDestination('stock-level');
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');

            // Alert the custom color filter code to sync its UI elements 
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });

            return;
          }

          updateSourceFromDestination('price');
          updateSourceFromDestination('price1');
          updateSourceFromDestination('ProductFormActionsRow');
          updateSourceFromDestination('ProductMediaGallery');
          updateSourceFromDestination('stock-level');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Sku1', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('MaterialAccordion');
          updateSourceFromDestination('FabricWeightAccordion');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          let variantNameDest = this.querySelector(`#VariantName-${this.dataset.section}`);

          if (variantNameDest) {
            variantNameDest.innerHTML = variant.title;
          }

          this.productForm?.toggleSubmitButton(
            false,
            window.variantStrings.soldOut
          );

          if (window._swat) {
            window._swat.initializeActionButtons("body");
          }

          initSizeGuideTrigger();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updatePricePerVariant(productUrl, variant) {
        fetch(`${productUrl}?variant=${variant.id}&section_id=product-cross-sell-card-price`).then(response => response.text()).then((response) => {
          let priceHtml = response;
          let destination = document.querySelector(`#Price-${this.sectionId}`);

          if (destination) {
            destination.innerHTML = priceHtml;
          }
        });
      }

      updateCardPrice(productUrl) {
        fetch(`${productUrl}?section_id=product-card-price`).then(response => response.text()).then((response) => {
          let priceHtml = response;
          let destination = document.querySelector(`#Price-${this.sectionId}`);

          if (destination) {
            destination.innerHTML = priceHtml;
          }
        });
      }

      updateVariantInputs(variantId) {
        const selectors = [];

        if (this.dataset.formId) {
          selectors.push(`#${this.dataset.formId}`);
          selectors.push(`#${this.dataset.formId}-installment`);
        } else {
          selectors.push(`#product-form-${this.dataset.section}`);
          selectors.push(`#product-form-installment-${this.dataset.section}`);
        }

        this.querySelectorAll(selectors.join(', ')).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      // FIX: Full 360-degree scanner (checks right, then wraps around to check left)
      setUnavailable() {
        const sizeInputs = Array.from(document.querySelectorAll('.product-form__input--pill--size input'));
        const currentIndex = sizeInputs.findIndex(input => input.checked);
        
        // 1. First, try moving one step to the right
        if (currentIndex !== -1 && currentIndex < sizeInputs.length - 1) {
          sizeInputs[currentIndex + 1].click();
        } 
        // 2. If we are at the end, wrap around to the far left and start over
        else if (currentIndex === sizeInputs.length - 1 && !this.hasWrappedAround) {
          this.hasWrappedAround = true; // Set a flag so we don't loop endlessly if everything is missing
          sizeInputs[0].click();
        } 
        // 3. If we've done a full circle and found absolutely nothing, hide the UI safely
        else {
          this.hasWrappedAround = false; // Reset the flag
          this.hideUnavailableUI();
        }
      }

      hideUnavailableUI() {
        this.hasWrappedAround = false; // Reset flag on exit
        this.isHopingVariants = false;
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules', 'stock-level']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('temp-hidden'));
      }

      hideUnavailableUI() {
        this.isHopingVariants = false;
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules', 'stock-level']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('temp-hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery-viewer ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery-viewer ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

        if (this.quantitySelect) {
          this.quantitySelect.value = min;
          this.quantitySelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        if (this.querySelector('.quantity__rules-cart .loading__spinner'))  {
          this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        }
        
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (this.querySelector('.quantity__rules-cart .loading__spinner')) {
              this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden')
            }
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        this.quantitySelect = this.quantityForm.querySelector('select[name="quantity"]');
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}